import { For, Show, createSignal } from "solid-js";
import { createMutation } from "@tanstack/solid-query";
import { updateSponsorLead, type FollowUpAction } from "~/lib/api";

export const FOLLOW_UP_OPTIONS: { id: FollowUpAction; label: string }[] = [
  { id: "email", label: "Email follow-up" },
  { id: "demo", label: "Book a demo" },
  { id: "materials", label: "Send materials" },
  { id: "call", label: "Call this week" },
  { id: "linkedin", label: "Connect on LinkedIn" },
  { id: "pass", label: "Not a fit" },
];

type Props = {
  scanId: string;
  initialNote?: string;
  initialRating?: number | null;
  initialFollowUp?: FollowUpAction | null;
  onSaved?: () => void;
};

export function SponsorLeadCapture(props: Props) {
  const [note, setNote] = createSignal(props.initialNote ?? "");
  const [rating, setRating] = createSignal<number | null>(props.initialRating ?? null);
  const [followUp, setFollowUp] = createSignal<FollowUpAction | null>(props.initialFollowUp ?? null);
  const [saved, setSaved] = createSignal(false);

  const saveMutation = createMutation(() => ({
    mutationFn: () =>
      updateSponsorLead(props.scanId, {
        note: note(),
        rating: rating(),
        follow_up: followUp(),
      }),
    onSuccess: () => {
      setSaved(true);
      props.onSaved?.();
    },
  }));

  function toggleRating(value: number) {
    setRating((prev) => (prev === value ? null : value));
    setSaved(false);
  }

  function toggleFollowUp(id: FollowUpAction) {
    setFollowUp((prev) => (prev === id ? null : id));
    setSaved(false);
  }

  return (
    <div class="sponsor-lead-capture">
      <p class="field-label">Lead rating</p>
      <div class="lead-rating" role="group" aria-label="Rate this lead from 1 to 5">
        <For each={[1, 2, 3, 4, 5]}>
          {(value) => (
            <button
              type="button"
              class={`lead-rating-star${rating() != null && value <= rating()! ? " active" : ""}`}
              aria-label={`${value} star${value === 1 ? "" : "s"}`}
              aria-pressed={rating() === value}
              onClick={() => toggleRating(value)}
            >
              ★
            </button>
          )}
        </For>
        <Show when={rating() != null}>
          <button type="button" class="btn btn-ghost lead-rating-clear" onClick={() => toggleRating(rating()!)}>
            Clear
          </button>
        </Show>
      </div>

      <p class="field-label" style={{ "margin-top": "12px" }}>
        Follow-up
      </p>
      <div class="interest-grid">
        <For each={FOLLOW_UP_OPTIONS}>
          {(option) => (
            <button
              type="button"
              class={`interest-chip${followUp() === option.id ? " selected" : ""}`}
              onClick={() => toggleFollowUp(option.id)}
            >
              {option.label}
            </button>
          )}
        </For>
      </div>

      <label class="field scan-lead-note">
        <span>Your lead note</span>
        <span class="muted" style={{ "font-size": "0.85rem" }}>
          Private to your sponsor team — who they are, context from the booth.
        </span>
        <textarea
          rows={3}
          maxLength={500}
          value={note()}
          onInput={(e) => {
            setNote(e.currentTarget.value);
            setSaved(false);
          }}
          placeholder="e.g. VP Data at Acme — wants Flink demo next week"
        />
      </label>

      <div class="scan-note-actions">
        <button
          type="button"
          class="btn btn-primary"
          disabled={saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
        >
          {saveMutation.isPending ? "Saving…" : "Save lead"}
        </button>
        <Show when={saved()}>
          <span class="muted">Saved</span>
        </Show>
        <Show when={saveMutation.isError}>
          <span class="muted">{(saveMutation.error as Error).message}</span>
        </Show>
      </div>
    </div>
  );
}